import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, {
  buildOrganizationSchema,
  buildWebPageSchema,
} from "../components/seo/StructuredData";
import Footer from "../components/Footer";
import AboutHero from "../components/about/AboutHero";
import AboutStory from "../components/about/AboutStory";
import AboutWhyHookora from "../components/about/AboutWhyHookora";
import AboutCTA from "../components/about/AboutCTA";
import useThemeMode from "../hooks/useThemeMode";
import useI18n from "../i18n/useI18n";

function AboutPage() {
  const { isDark } = useThemeMode();
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const featuredGuides = useMemo(
    () => [
      {
        title: tt("home.guides.whatIsHookora.title", "What Is Hookora?"),
        description: tt(
          "about.guides.whatIsHookora.description",
          "Learn what Hookora is, how it works, and why it helps creators move from idea to stronger content faster."
        ),
        to: "/blog/what-is-hookora",
      },
      {
        title: tt(
          "home.guides.nigerianCreators.title",
          "Hookora for Nigerian Creators"
        ),
        description: tt(
          "about.guides.nigerianCreators.description",
          "See how Hookora helps Nigerian creators build stronger hooks, titles, captions, scripts, and better content packaging."
        ),
        to: "/blog/hookora-for-nigerian-creators",
      },
      {
        title: tt(
          "home.guides.writeHooksFaster.title",
          "How Hookora Helps Creators Write Hooks Faster"
        ),
        description: tt(
          "about.guides.writeHooksFaster.description",
          "Explore how Hookora helps creators write hooks faster with clearer angles and stronger first lines."
        ),
        to: "/blog/how-hookora-helps-creators-write-hooks-faster",
      },
    ],
    [t]
  );

  const colors = {
    sectionBg: isDark ? "#020617" : "#f8fafc",
    cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
    cardSoftBg: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
    border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
    borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
    text: isDark ? "#f8fafc" : "#111827",
    textSoft: isDark ? "#cbd5e1" : "#374151",
    textMuted: isDark ? "#94a3b8" : "#6b7280",
    badgeBg: isDark ? "rgba(109,40,217,0.18)" : "#f5f3ff",
    badgeText: isDark ? "#d8b4fe" : "#6d28d9",
    shadow: isDark
      ? "0 14px 40px rgba(2, 6, 23, 0.45)"
      : "0 12px 30px rgba(15, 23, 42, 0.06)",
  };

  return (
    <div
      style={{
        background: isDark ? "#020617" : "#f8fafc",
        backgroundImage: isDark
          ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
          : "none",
        minHeight: "100vh",
      }}
    >
      <SeoHead
        title={tt(
          "about.seoTitle",
          "About Hookora | AI Workflow Platform for Creators"
        )}
        description={tt(
          "about.seoDescription",
          "Learn what Hookora is, who it helps, and how Hookora makes content creation easier with hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text tools."
        )}
        path="/about"
      />

      <StructuredData id="hookora-about-org-schema" data={buildOrganizationSchema()} />
      <StructuredData
        id="hookora-about-page-schema"
        data={buildWebPageSchema({
          name: tt("about.heroBadge", "About Hookora"),
          description: tt(
            "about.schemaDescription",
            "Learn what Hookora is, who it helps, and how Hookora supports creators with a smoother content workflow."
          ),
          url: "https://hookora.app/about",
          type: "AboutPage",
        })}
      />

      <Navbar />
      <AboutHero />
      <AboutStory />
      <AboutWhyHookora />

      <section style={{ padding: "8px 20px 56px", background: colors.sectionBg }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "8px 14px",
                borderRadius: "999px",
                background: colors.badgeBg,
                color: colors.badgeText,
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              {tt("home.featuredGuidesBadge", "Featured Hookora Guides")}
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                color: colors.text,
                fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
                lineHeight: "1.1",
              }}
            >
              {tt(
                "about.guidesSectionTitle",
                "Learn more about Hookora and how it helps creators"
              )}
            </h2>

            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                fontSize: "16px",
                lineHeight: "1.75",
                maxWidth: "760px",
                marginInline: "auto",
              }}
            >
              {tt(
                "about.guidesSectionDescription",
                "These quick guides explain what Hookora does, who it helps, and how it makes hook writing and content workflow feel clearer and faster."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "18px",
            }}
          >
            {featuredGuides.map((guide) => (
              <article
                key={guide.to}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "24px",
                  padding: "24px",
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px",
                    color: colors.text,
                    fontSize: "24px",
                    lineHeight: "1.25",
                  }}
                >
                  {guide.title}
                </h3>

                <p
                  style={{
                    margin: "0 0 18px",
                    color: colors.textSoft,
                    fontSize: "15px",
                    lineHeight: "1.75",
                  }}
                >
                  {guide.description}
                </p>

                <Link
                  to={guide.to}
                  style={{
                    display: "inline-flex",
                    textDecoration: "none",
                    background: colors.cardSoftBg,
                    color: colors.text,
                    padding: "12px 16px",
                    borderRadius: "12px",
                    fontWeight: "800",
                    border: `1px solid ${colors.borderStrong}`,
                  }}
                >
                  {tt("common.readGuide", "Read Guide")}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <AboutCTA />
      <Footer />
    </div>
  );
}

export default AboutPage;